import { formatDateShort } from '@/lib/format';
import {
  formatTravelEsimData,
  formatTravelEsimDuration,
  travelEsimLineStatus,
  travelEsimStatusClasses,
} from '@/lib/travel-esim';
import { cn } from '@/lib/utils';
import type { TravelEsimDetail, TravelEsimStayState } from '@octave/contracts';
import { Link } from 'react-router-dom';

interface LineTravelEsimPlansTableProps {
  esim: TravelEsimDetail;
  tenantSlug: string;
  /** `YYYY-MM-DD`, to classify each stay. */
  today: string;
}

const STAY_LABEL: Record<TravelEsimStayState, string> = {
  upcoming: 'À venir',
  ongoing: 'En cours',
  past: 'Terminé',
};

const STAY_CLASSES: Record<TravelEsimStayState, string> = {
  upcoming: 'bg-amber-100 text-amber-800',
  ongoing: 'bg-emerald-600 text-white',
  past: 'bg-slate-200 text-slate-600',
};

function stayStateOf(
  departureDate: string,
  returnDate: string,
  today: string,
): TravelEsimStayState {
  if (today < departureDate) return 'upcoming';
  if (today > returnDate) return 'past';
  return 'ongoing';
}

/** « Forfaits internationaux »: the plans loaded on the eSIM, as a table. */
export function LineTravelEsimPlansTable({
  esim,
  tenantSlug,
  today,
}: LineTravelEsimPlansTableProps) {
  return (
    <div className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900">Forfaits internationaux</h2>
        <p className="text-xs text-slate-400">
          {esim.plansCount > 1
            ? `${esim.plansCount} voyages`
            : `${esim.plansCount} voyage`}
        </p>
      </div>

      {esim.plans.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Aucun forfait chargé sur cette eSIM.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="pb-2 pr-3 font-medium">Pays</th>
                <th className="pb-2 pr-3 font-medium">Forfait</th>
                <th className="pb-2 pr-3 font-medium">Commande</th>
                <th className="pb-2 pr-3 font-medium whitespace-nowrap">Côté Octave</th>
                <th className="pb-2 font-medium whitespace-nowrap">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {esim.plans.map((plan) => {
                const { metadata } = plan.item;
                if (metadata.type !== 'TRAVEL_ESIM') return null;
                const state = stayStateOf(
                  metadata.departureDate,
                  metadata.returnDate,
                  today,
                );
                const status = travelEsimLineStatus(metadata);
                return (
                  <tr
                    key={plan.item.id}
                    className={cn('align-middle', state === 'past' && 'opacity-60')}
                  >
                    <td className="py-3 pr-3 font-medium text-slate-900">
                      {metadata.country}
                    </td>
                    <td className="py-3 pr-3 text-slate-600">
                      {formatTravelEsimData(metadata.dataLimitGb)} pendant{' '}
                      {formatTravelEsimDuration(metadata.stayDurationDays)}
                      <div className="text-xs text-slate-400">
                        Du {formatDateShort(metadata.departureDate)} au{' '}
                        {formatDateShort(metadata.returnDate)}
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-slate-600 whitespace-nowrap">
                      <Link
                        to={`/o/${tenantSlug}/orders/${plan.orderId}`}
                        className="underline hover:text-slate-900"
                      >
                        {plan.orderNumber}
                      </Link>
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center justify-center rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap ${travelEsimStatusClasses(status.tone)}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${STAY_CLASSES[state]}`}
                      >
                        {STAY_LABEL[state]}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
